/**
 * Thresholds for maximum parameter calculations.
 *
 * SAFE_HF is the minimum health factor we allow after the trade —
 * going below this puts the account dangerously close to liquidation.
 */
export enum MaxParamThresholds {
  MIN_HF = 1.0,
  SAME_OPEN_HF = 1.1,
  SAFE_HF = 1.01,
}

/**
 * Compute the maximum dollar amount a user can borrow-swap-deposit
 * when opening a leveraged position, without dropping the health
 * factor below `SAFE_HF`.
 *
 * For a "same-asset" loop (borrow and deposit the same token) we
 * use the stricter `SAME_OPEN_HF` threshold because the position
 * is more sensitive to rate changes.
 *
 * @param borrowDiscountedCollateral  Current risk-adjusted collateral (for borrow capacity)
 * @param collateral                  Current collateral value (for health factor)
 * @param debt                        Current total debt
 * @param cfOut                       Collateral factor of the deposited (out) asset
 * @param bfIn                        Borrow factor of the borrowed (in) asset
 * @param sameAsset                   Whether in and out assets are the same token
 * @returns Maximum additional dollar amount that can be borrowed
 */
export function getMaxAmountOpen(
  borrowDiscountedCollateral: number,
  collateral: number,
  debt: number,
  cfOut: number,
  bfIn: number,
  sameAsset = false,
): number {
  if (sameAsset) {
    return (
      (collateral - MaxParamThresholds.SAME_OPEN_HF * debt) /
      (MaxParamThresholds.SAME_OPEN_HF * bfIn - cfOut)
    )
  }

  return (
    (borrowDiscountedCollateral - MaxParamThresholds.SAFE_HF * debt) /
    (MaxParamThresholds.SAFE_HF * bfIn - cfOut)
  )
}
